/**
 * =============================================================================
 * IDS CRM Web - Parts used on a job or ticket (spare parts, Push 2)
 * =============================================================================
 *
 * The "Parts used" list on the job and ticket pages (build spec 8.5), from
 * GET /parts?task_id= or ?ticket_id=. A table on a desktop, cards on a phone
 * (one table, laid out as cards by the stylesheet).
 *
 *  - Everyone sees each part, its quantity, the robot (jobs), whether it is
 *    covered (warranty or Repair & Replace) and who added it on which day.
 *    The covered flag is not a cost or a charge, so the API never strips it.
 *  - Money roles (canSeePartMoney) also see its cost (the figure Financials
 *    counts), its charge and "Needs a price". Engineers never see cost or
 *    charge: the API strips them, and this list does not ask for them.
 *  - Team leaders and up (canCorrectParts) can Correct a live part (quantity,
 *    robot, cover, charge, the name of a part not in the list, and the cost
 *    of a part that needs a price) or Void it with a reason. Parts are never
 *    deleted: a voided part stays, struck through, with who and why.
 *  - allowAdd (the job page only): "Add a part" records a part missed from
 *    Log Work, or a crew member's line that never reached the server before
 *    the job was closed. It sends a line id, so a double tap records it once.
 *
 * The Void dialog is this component's own, not useConfirm: ConfirmDialog
 * cannot hold a reason box, cannot disable its button, and closes before its
 * onConfirm runs, so a refusal would lose the reason.
 *
 * A read that failed is never shown as "No parts recorded.", and Add a part
 * is not offered until the list has been read: a team leader looking at an
 * empty list after a blip in signal would otherwise record a part twice.
 * readPartsUsed reads every page of the job's or ticket's parts, not only
 * the first 100.
 * =============================================================================
 */

import React, { useState } from 'react';
import SparePartsPicker from './SparePartsPicker';
import { ukDayLabelOf, firstNameOf } from './SavedLogWorkPanel';
import { newUuid } from '../hooks/useSavedLogWork';
import { formatMoney } from '../utils/money';
import { ukTodayKey } from '../utils/dates';
import { canCorrectParts, canSeePartMoney } from '../utils/permissions';
import {
  costGbpText,
  normaliseQuantityText,
  parseQuantity,
  poundsToPence,
  quantityText,
  QUANTITY_HINT
} from '../utils/spareLines';
import './PartsUsedList.css';

const WHOLE_JOB = '__whole_job__';

const lower = (value) => String(value ?? '').trim().toLowerCase();

const quantityOf = (row) => quantityText(row.quantity_text ?? row.quantity);

const firstNameOrNull = (name) => (String(name || '').trim() ? firstNameOf(name) : null);

// GET /parts gives at most 100 rows a page, in the order they were fitted.
export const PARTS_PAGE_SIZE = 100;
// 5,000 parts on one job or ticket: far beyond any real one, and a stop if
// the server ever answers every page with the same rows.
const MAX_PARTS_PAGES = 50;

/**
 * Every Parts used row of one job or ticket, voided ones included: page after
 * page until a short page. Rows are kept once by part_id, in case a part was
 * recorded between two pages.
 *
 * @param {Function} authFetch
 * @param {{taskId?: string, ticketId?: string}} where
 * @returns {Promise<Array<object>|null>} null when any page could not be read
 */
export async function readPartsUsed(authFetch, { taskId = null, ticketId = null } = {}) {
  const filter = taskId
    ? `task_id=${encodeURIComponent(taskId)}`
    : `ticket_id=${encodeURIComponent(ticketId)}`;
  const rows = [];
  const seen = new Set();
  for (let page = 1; page <= MAX_PARTS_PAGES; page += 1) {
    let data = null;
    try {
      const response = await authFetch(`/api/v1/parts?${filter}&limit=${PARTS_PAGE_SIZE}${page > 1 ? `&page=${page}` : ''}`);
      if (!response || !response.ok) return null;
      data = await response.json().catch(() => null);
    } catch (err) {
      return null;
    }
    if (!data || !Array.isArray(data.data)) return null;
    let added = 0;
    for (const row of data.data) {
      const id = row && row.part_id ? lower(row.part_id) : null;
      if (id && seen.has(id)) continue;
      if (id) seen.add(id);
      rows.push(row);
      added += 1;
    }
    if (data.data.length < PARTS_PAGE_SIZE || added === 0) break;
  }
  return rows;
}

/** The sentence for a Parts used list that could not be read. */
export const partsLoadFailedMessage = (subject) => (
  `The parts used on this ${subject === 'ticket' ? 'ticket' : 'job'} could not be loaded. Check your signal and try again.`
);

/**
 * 12:00 UK time on a UK day, as an ISO instant: the fitted time sent for a
 * part added for an earlier day (POST refuses a time in the future).
 */
export function ukNoonIso(dayKey) {
  const [year, month, day] = String(dayKey).split('-').map(Number);
  const noonUtc = Date.UTC(year, month - 1, day, 12, 0, 0);
  try {
    const londonHour = Number(new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London', hour: '2-digit', hourCycle: 'h23'
    }).format(new Date(noonUtc)));
    return new Date(noonUtc - (londonHour - 12) * 3600000).toISOString();
  } catch (error) {
    return new Date(noonUtc).toISOString();
  }
}

/** Does this part have no usable cost, so the Correct dialog may take one (8.3)? */
const takesCost = (row) => row.charge_basis === 'none'
  || Boolean(row.needs_price)
  || (row.charge_basis == null && row.unit_cost_gbp == null && !Number(row.unit_cost_minor_units));

async function answerOf(response) {
  try {
    return await response.json();
  } catch (error) {
    return {};
  }
}

function Dialog({ title, titleId, onClose, children, footer, className = '' }) {
  return (
    <div className="modal-overlay parts-used-dialog-overlay">
      <div
        className={`modal-content parts-used-dialog ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={event => event.stopPropagation()}
      >
        <div className="modal-header">
          <h3 id={titleId}>{title}</h3>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>
        <div className="modal-body parts-used-dialog-body">{children}</div>
        <div className="modal-footer parts-used-dialog-footer">{footer}</div>
      </div>
    </div>
  );
}

function RobotSelect({ id, robots, value, onChange, label }) {
  return (
    <select id={id} aria-label={label} value={value} onChange={event => onChange(event.target.value)}>
      <option value="" disabled>Which robot?</option>
      {robots.map(robot => (
        <option key={robot.asset_id} value={robot.asset_id}>{robot.asset_ref}</option>
      ))}
      <option value={WHOLE_JOB}>Whole job (fixings, cable, consumables)</option>
    </select>
  );
}

function CorrectDialog({ row, robots, showRobot, authFetch, onClose, onSaved }) {
  const initialCharge = row.unit_charge_minor_units != null ? (Number(row.unit_charge_minor_units) / 100).toFixed(2) : '';
  const initialRobot = row.whole_job ? WHOLE_JOB : (row.asset_id ? lower(row.asset_id) : '');
  const [quantity, setQuantity] = useState(quantityOf(row));
  const [robot, setRobot] = useState(initialRobot);
  const [covered, setCovered] = useState(Boolean(row.is_warranty));
  const [charge, setCharge] = useState(initialCharge);
  const [name, setName] = useState(row.part_name || '');
  const [cost, setCost] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const showCost = takesCost(row);
  const robotChoices = showRobot ? robots : [];

  const save = async () => {
    const body = {};
    const parsed = parseQuantity(normaliseQuantityText(quantity));
    if (!parsed.ok) { setError(QUANTITY_HINT); return; }
    if (quantityText(parsed.text) !== quantityOf(row)) body.quantity = Number(parsed.text);
    if (robotChoices.length > 0 && robot !== initialRobot && robot) {
      if (robot === WHOLE_JOB) body.whole_job = true;
      else { body.asset_id = robot; body.whole_job = false; }
    }
    if (covered !== Boolean(row.is_warranty)) body.is_warranty = covered;
    if (charge.trim() !== initialCharge && charge.trim() !== '') {
      const pence = poundsToPence(charge);
      if (pence === null) { setError('Enter the charge per unit in pounds, such as 12.50.'); return; }
      if (pence !== Number(row.unit_charge_minor_units)) body.unit_charge_minor_units = pence;
    }
    if (showCost && cost.trim() !== '') {
      const text = costGbpText(cost);
      if (text === null) { setError('Enter the cost per unit in pounds, with at most 4 decimal places, such as 4.95.'); return; }
      body.unit_cost_gbp = text;
    }
    if (row.not_in_list) {
      const trimmed = name.trim();
      if (!trimmed) { setError('A part needs a name.'); return; }
      if (trimmed !== String(row.part_name || '').trim()) body.part_name = trimmed;
    }
    if (Object.keys(body).length === 0) { onClose(); return; }

    setSaving(true);
    setError(null);
    try {
      const response = await authFetch(`/api/v1/parts/${row.part_id}`, { method: 'PUT', body: JSON.stringify(body) });
      const data = await answerOf(response);
      if (!response.ok) {
        setError(data.message || 'The correction could not be saved. Try again.');
        return;
      }
      onSaved();
    } catch (err) {
      setError('The correction could not be saved. Check your signal and try again.');
    } finally {
      setSaving(false);
    }
  };

  const idBase = `correct-part-${row.part_id}`;
  return (
    <Dialog
      title={`Correct ${row.part_name}`}
      titleId={`${idBase}-title`}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="parts-used-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="parts-used-btn primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save correction'}
          </button>
        </>
      )}
    >
      {error && <div className="parts-used-dialog-error" role="alert">{error}</div>}
      <div className="parts-used-field">
        <label htmlFor={`${idBase}-qty`}>Quantity</label>
        <input id={`${idBase}-qty`} type="text" inputMode="decimal" autoComplete="off" value={quantity} onChange={event => setQuantity(event.target.value)} />
      </div>
      {robotChoices.length > 0 && (
        <div className="parts-used-field">
          <label htmlFor={`${idBase}-robot`}>Robot</label>
          <RobotSelect id={`${idBase}-robot`} label="Robot" robots={robotChoices} value={robot} onChange={setRobot} />
        </div>
      )}
      <label className="parts-used-check">
        <input type="checkbox" checked={covered} onChange={event => setCovered(event.target.checked)} />
        Covered by warranty or Repair &amp; Replace
      </label>
      <div className="parts-used-field">
        <label htmlFor={`${idBase}-charge`}>Charge per unit (£)</label>
        <input id={`${idBase}-charge`} type="text" inputMode="decimal" autoComplete="off" value={charge} onChange={event => setCharge(event.target.value)} />
        <span className="parts-used-hint">Setting a charge here replaces the way the part was priced.</span>
      </div>
      {row.not_in_list && (
        <div className="parts-used-field">
          <label htmlFor={`${idBase}-name`}>Name</label>
          <input id={`${idBase}-name`} type="text" autoComplete="off" maxLength={255} value={name} onChange={event => setName(event.target.value)} />
        </div>
      )}
      {showCost && (
        <div className="parts-used-field">
          <label htmlFor={`${idBase}-cost`}>Cost per unit (£)</label>
          <input id={`${idBase}-cost`} type="text" inputMode="decimal" autoComplete="off" value={cost} onChange={event => setCost(event.target.value)} />
          <span className="parts-used-hint">The part is then charged at this cost plus the markup.</span>
        </div>
      )}
    </Dialog>
  );
}

function VoidDialog({ row, authFetch, onClose, onSaved }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const reasonOk = reason.trim().length >= 5;
  const idBase = `void-part-${row.part_id}`;

  const submit = async () => {
    if (!reasonOk) return;
    setSaving(true);
    setError(null);
    try {
      const response = await authFetch(`/api/v1/parts/${row.part_id}/void`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason.trim() })
      });
      const data = await answerOf(response);
      if (!response.ok) {
        setError(data.message || 'The part could not be voided. Try again.');
        return;
      }
      onSaved();
    } catch (err) {
      setError('The part could not be voided. Check your signal and try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      title="Void this part?"
      titleId={`${idBase}-title`}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="parts-used-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="parts-used-btn danger" onClick={submit} disabled={!reasonOk || saving}>
            {saving ? 'Voiding…' : 'Void part'}
          </button>
        </>
      )}
    >
      {error && <div className="parts-used-dialog-error" role="alert">{error}</div>}
      <p className="parts-used-dialog-text">
        {row.part_name} ×{quantityOf(row)} will no longer count in costs or charges. The record is kept with your reason.
      </p>
      <div className="parts-used-field">
        <label htmlFor={`${idBase}-reason`}>Reason (at least 5 characters)</label>
        <textarea id={`${idBase}-reason`} rows={3} maxLength={500} value={reason} onChange={event => setReason(event.target.value)} />
      </div>
    </Dialog>
  );
}

function AddPartDialog({ taskId, robots, catalogue, authFetch, onClose, onSaved }) {
  const today = ukTodayKey();
  const [picked, setPicked] = useState(null);
  const [quantity, setQuantity] = useState('1');
  const [robot, setRobot] = useState('');
  const [day, setDay] = useState(today);
  // One line id per dialog: a double tap, or a retry after a lost reply,
  // records the part once.
  const [lineId] = useState(() => newUuid());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const several = robots.length > 1;

  const submit = async () => {
    if (!picked) { setError('Choose the part from the list, or Part not in the list.'); return; }
    const parsed = parseQuantity(normaliseQuantityText(quantity));
    if (!parsed.ok) { setError(QUANTITY_HINT); return; }
    if (several && !robot) { setError('Say which robot the part was fitted to, or choose Whole job.'); return; }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) { setError('Enter the day it was fitted.'); return; }
    if (day > today) { setError('The day it was fitted cannot be after today.'); return; }

    const body = {
      task_id: taskId,
      part_name: picked.name,
      quantity: Number(parsed.text),
      fitted_at: day === today ? new Date().toISOString() : ukNoonIso(day),
      client_line_id: lineId
    };
    if (picked.notInList) {
      body.not_in_list = true;
    } else {
      body.catalogue_id = picked.id;
      if (picked.number) body.part_number = picked.number;
    }
    if (several) {
      if (robot === WHOLE_JOB) body.whole_job = true;
      else body.asset_id = robot;
    }

    setSaving(true);
    setError(null);
    try {
      const response = await authFetch('/api/v1/parts', { method: 'POST', body: JSON.stringify(body) });
      const data = await answerOf(response);
      if (!response.ok) {
        setError(data.message || 'The part could not be added. Try again.');
        return;
      }
      onSaved();
    } catch (err) {
      setError('The part could not be added. Check your signal and try again; it will not be recorded twice.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      title="Add a part"
      titleId="add-part-title"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="parts-used-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="parts-used-btn primary" onClick={submit} disabled={saving}>
            {saving ? 'Adding…' : 'Add part'}
          </button>
        </>
      )}
    >
      {error && <div className="parts-used-dialog-error" role="alert">{error}</div>}
      <p className="parts-used-dialog-text">
        For a part missed from Log Work, or one a crew member could not send before the job was closed.
      </p>
      <div className="parts-used-field">
        <label htmlFor="add-part-search">Part</label>
        {picked ? (
          <div className="parts-used-picked">
            <span>
              <strong>{picked.name}</strong>
              {picked.notInList ? ' · Not in the list' : (picked.number ? ` · ${picked.number}` : '')}
            </span>
            <button type="button" className="parts-used-btn" onClick={() => setPicked(null)}>Change</button>
          </div>
        ) : (
          <SparePartsPicker
            id="add-part-search"
            items={catalogue && Array.isArray(catalogue.items) ? catalogue.items : []}
            status={catalogue ? catalogue.status : 'none'}
            onPick={setPicked}
          />
        )}
      </div>
      <div className="parts-used-field">
        <label htmlFor="add-part-qty">Quantity</label>
        <input id="add-part-qty" type="text" inputMode="decimal" autoComplete="off" value={quantity} onChange={event => setQuantity(event.target.value)} />
      </div>
      {several && (
        <div className="parts-used-field">
          <label htmlFor="add-part-robot">Robot</label>
          <RobotSelect id="add-part-robot" label="Robot" robots={robots} value={robot} onChange={setRobot} />
        </div>
      )}
      <div className="parts-used-field">
        <label htmlFor="add-part-day">The day it was fitted</label>
        <input id="add-part-day" type="date" max={today} value={day} onChange={event => setDay(event.target.value)} />
      </div>
    </Dialog>
  );
}

/**
 * @param {object} props
 * @param {Array<object>} props.rows - GET /parts rows (voided ones included)
 * @param {Array<{asset_id, asset_ref}>} [props.robots] - the job's robots
 * @param {boolean} [props.showRobot] - jobs: show the Robot column
 * @param {boolean} [props.allowAdd] - the job page: offer Add a part (team leader and up)
 * @param {string|null} [props.taskId]
 * @param {{items, status}|null} [props.catalogue] - useSparePartsList(), for Add a part
 * @param {{role: string}} props.currentUser
 * @param {Function} props.authFetch
 * @param {() => void} [props.onChanged] - after a correction, a void or an added part
 * @param {'loading'|'ready'|'failed'} [props.status] - the page's read of the rows
 * @param {() => void} [props.onRetry] - Try again after a failed read
 * @param {'job'|'ticket'} [props.subject] - for the failed-read sentence
 */
function PartsUsedList({
  rows,
  robots = [],
  showRobot = false,
  allowAdd = false,
  taskId = null,
  catalogue = null,
  currentUser,
  authFetch,
  onChanged,
  status = 'ready',
  onRetry,
  subject = 'job'
}) {
  const [correcting, setCorrecting] = useState(null);
  const [voiding, setVoiding] = useState(null);
  const [adding, setAdding] = useState(false);
  const role = currentUser && currentUser.role;
  const money = canSeePartMoney(role);
  const canCorrect = canCorrectParts(role);
  const list = Array.isArray(rows) ? rows : [];
  const liveCount = list.filter(row => !row.voided_at).length;
  // Until a read has answered (or when the latest one failed) the list is
  // not known: no count, no "No parts recorded." and no Add a part.
  const failed = status === 'failed';
  const firstLoad = status === 'loading' && list.length === 0;
  const known = !failed && !firstLoad;
  const jobRobots = (Array.isArray(robots) ? robots : [])
    .filter(robot => robot && robot.asset_id)
    .map(robot => ({ asset_id: lower(robot.asset_id), asset_ref: robot.asset_ref || 'Robot' }));

  const changed = () => {
    setCorrecting(null);
    setVoiding(null);
    setAdding(false);
    if (typeof onChanged === 'function') onChanged();
  };

  const moneyText = (minor, row) => (minor === null || minor === undefined
    ? '—'
    : formatMoney(minor, row.currency || 'GBP'));

  return (
    <div className="parts-used-list">
      <div className="parts-used-list-header">
        <h3>{known ? `Parts used (${liveCount})` : 'Parts used'}</h3>
      </div>

      {failed && (
        <div className="parts-used-list-failed" role="status">
          <p>{partsLoadFailedMessage(subject)}</p>
          {typeof onRetry === 'function' && (
            <button type="button" className="parts-used-btn" onClick={() => onRetry()}>Try again</button>
          )}
        </div>
      )}
      {firstLoad && <p className="parts-used-list-empty">Loading the parts used…</p>}

      {!known ? null : list.length === 0 ? (
        <p className="parts-used-list-empty">No parts recorded.</p>
      ) : (
        <div className="parts-used-list-wrap">
          <table className="parts-used-list-table">
            <thead>
              <tr>
                <th>Part</th>
                <th>Qty</th>
                {showRobot && <th>Robot</th>}
                <th>Cover</th>
                {money && <th>Cost</th>}
                {money && <th>Charge</th>}
                <th>Added</th>
                {canCorrect && <th><span className="parts-used-sr">Actions</span></th>}
              </tr>
            </thead>
            <tbody>
              {list.map(row => {
                const voided = Boolean(row.voided_at);
                const adder = firstNameOrNull(row.added_by_name || row.engineer_name);
                const day = row.fitted_at ? ukDayLabelOf(row.fitted_at) : '';
                const voider = firstNameOrNull(row.voided_by_name) || 'someone';
                return (
                  <tr key={row.part_id} className={voided ? 'is-voided' : ''}>
                    <td data-label="Part" className="parts-used-part">
                      <span className="parts-used-name">{row.part_name}</span>
                      {row.part_number && <span className="parts-used-number"> · {row.part_number}</span>}
                      {(row.not_in_list || (money && row.needs_price && !voided)) && (
                        <span className="parts-used-badges">
                          {row.not_in_list && <span className="parts-used-badge">Not in the list</span>}
                          {money && row.needs_price && !voided && <span className="parts-used-badge is-price">Needs a price</span>}
                        </span>
                      )}
                      {voided && (
                        <span className="parts-used-voided">Voided by {voider}: {row.void_reason || 'no reason given'}</span>
                      )}
                    </td>
                    <td data-label="Qty">{quantityOf(row)}</td>
                    {showRobot && (
                      <td data-label="Robot">{row.whole_job ? 'Whole job' : (row.asset_ref || '—')}</td>
                    )}
                    <td data-label="Cover">{row.is_warranty ? 'Covered' : 'Chargeable'}</td>
                    {money && <td data-label="Cost">{moneyText(row.cost_minor_units, row)}</td>}
                    {money && <td data-label="Charge">{moneyText(row.total_charge_minor_units, row)}</td>}
                    <td data-label="Added">
                      {adder ? `Added by ${adder}` : 'Added'}{day ? ` · ${day}` : ''}
                    </td>
                    {canCorrect && (
                      <td className="parts-used-actions">
                        {!voided && (
                          <>
                            <button type="button" className="parts-used-btn" onClick={() => setCorrecting(row)} aria-label={`Correct ${row.part_name}`}>
                              Correct
                            </button>
                            <button type="button" className="parts-used-btn danger" onClick={() => setVoiding(row)} aria-label={`Void ${row.part_name}`}>
                              Void
                            </button>
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {allowAdd && canCorrect && taskId && known && (
        <div className="parts-used-add">
          <button type="button" className="parts-used-btn" onClick={() => setAdding(true)}>Add a part</button>
        </div>
      )}

      {correcting && (
        <CorrectDialog
          row={correcting}
          robots={jobRobots}
          showRobot={showRobot}
          authFetch={authFetch}
          onClose={() => setCorrecting(null)}
          onSaved={changed}
        />
      )}
      {voiding && (
        <VoidDialog row={voiding} authFetch={authFetch} onClose={() => setVoiding(null)} onSaved={changed} />
      )}
      {adding && (
        <AddPartDialog
          taskId={taskId}
          robots={jobRobots}
          catalogue={catalogue}
          authFetch={authFetch}
          onClose={() => setAdding(false)}
          onSaved={changed}
        />
      )}
    </div>
  );
}

export default PartsUsedList;
